import React, { useEffect, useState } from 'react';
import {
    Box, Typography, ToggleButton, ToggleButtonGroup,
    TextField, MenuItem, Button, Alert, CircularProgress, Paper
} from '@mui/material';
import { TaxService } from '../../services/tax.service';
import { ITaxSettings, TaxMode } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

type MonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
const MONTH_NUMBERS: MonthNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const TaxSettings: React.FC = () => {
    const { t } = useTranslation(['tax', 'common']);
    const { showSuccess, showError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [settings, setSettings] = useState<ITaxSettings>({
        institutionId, taxMode: 'SLAB', taxYearStartMonth: 1
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        TaxService.getTaxSettings(institutionId)
            .then(s => setSettings(s))
            .catch(() => { /* use defaults if not found */ })
            .finally(() => setLoading(false));
    }, [institutionId]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const saved = await TaxService.upsertTaxSettings({ ...settings, institutionId });
            setSettings(saved);
            showSuccess(t('settings.toast.saved'));
        } catch {
            showError(t('settings.toast.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box p={3}>
            <Typography variant="h5" mb={3}>{t('settings.title')}</Typography>
            <Paper sx={{ p: 3, maxWidth: 500 }}>
                <Typography variant="subtitle2" mb={1}>{t('settings.mode')}</Typography>
                <ToggleButtonGroup
                    exclusive
                    value={settings.taxMode}
                    onChange={(_, v) => v && setSettings(s => ({ ...s, taxMode: v as TaxMode }))}
                    sx={{ mb: 3 }}
                >
                    <ToggleButton value="SLAB">{t('settings.slab')}</ToggleButton>
                    <ToggleButton value="FLAT">{t('settings.flat')}</ToggleButton>
                </ToggleButtonGroup>

                {settings.taxMode === 'FLAT' && (
                    <TextField
                        label={t('settings.flatRate')}
                        type="number"
                        value={settings.flatRate ?? ''}
                        onChange={e => setSettings(s => ({ ...s, flatRate: parseFloat(e.target.value) }))}
                        fullWidth sx={{ mb: 3 }}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                )}

                <TextField
                    select label={t('settings.startMonth')}
                    value={settings.taxYearStartMonth}
                    onChange={e => setSettings(s => ({ ...s, taxYearStartMonth: parseInt(e.target.value) }))}
                    fullWidth sx={{ mb: 3 }}
                >
                    {MONTH_NUMBERS.map(m => <MenuItem key={m} value={m}>{t(`common:months.${m}`)}</MenuItem>)}
                </TextField>

                <Alert severity="info" sx={{ mb: 2 }}>
                    {t('settings.info')}
                </Alert>

                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? t('shared.saving') : t('settings.save')}
                </Button>
            </Paper>
        </Box>
    );
};

export default TaxSettings;
